import {
  type ChangeEvent,
  type DragEvent,
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

import AlertCircle from '@crehana/compass.icons.outline/AlertCircle';
import FileText from '@crehana/compass.icons.outline/FileText';
import Refresh from '@crehana/compass.icons.outline/Refresh';
import { Loader, ProgressBar } from '@crehana/compass.ui';
import { formatDuration } from '@crehana/date';
import { bytesToSize } from '@crehana/utils';

import useAssignUserTranslation from '../../pages/AssignUsersByCourse/hooks/useAssignUserTranslation';
import type { TFileProgress } from '../ExcelImporterStepper/context/types';

interface FileInputProps {
  accept?: string;
  file?: File;
  label?: string | ReactNode;
  labelError?: string;
  labelErrorExtension?: string;
  labelErrorExtensionDesc?: string;
  labelMaxSize?: string;
  labelReplaceFile?: string;
  maxSize?: number;
  progressFile?: TFileProgress;
  onLoadFile?: (file: File) => void;
  isLoading?: boolean;
}

const FileInputV2: FC<React.PropsWithChildren<FileInputProps>> = ({
  accept = '.xlsx',
  file,
  label,
  labelError,
  labelErrorExtension,
  labelErrorExtensionDesc,
  labelReplaceFile,
  labelMaxSize,
  maxSize = 500,
  onLoadFile,
  progressFile,
  isLoading,
}) => {
  const { t } = useAssignUserTranslation();
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [error, setError] = useState('');
  const [errorExtension, setErrorExtension] = useState('');

  const durationUpFile = progressFile?.remainingSeconds
    ? formatDuration(progressFile.remainingSeconds)
    : t('FILE_DURATION_CUSTOM');

  const handleState = (isError?: boolean) => {
    setError(isError ? labelError || 'error' : '');
    setFileName('');
    setFileSize(0);
  };

  const handleSetErrorExtension = (isError?: boolean) => {
    setErrorExtension(isError ? labelErrorExtension || 'error' : '');
    setFileName('');
    setFileSize(0);
  };

  const getExtensionFile = (nameFile: string) => {
    return nameFile.split('.')?.pop()?.toLowerCase();
  };

  const handleOnChange = (
    event?: ChangeEvent<HTMLInputElement>,
    drag?: DragEvent<HTMLLabelElement>,
  ) => {
    drag?.preventDefault();

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!event && !drag && !file) return;

    const fileDragged = drag
      ? drag.dataTransfer.files && drag?.dataTransfer.files[0]
      : file || null;

    const fileSelected =
      event && event.target.files?.length ? event.target.files[0] : file;

    const attachment = fileSelected || fileDragged;

    setError('');
    setFileName(attachment?.name || '');
    setFileSize(attachment?.size || 0);

    if (attachment) {
      const size = attachment.size / 1024;
      const extension = getExtensionFile(attachment.name);

      if (size > maxSize) {
        handleState(true);
      } else if (extension !== 'xlsx') {
        handleSetErrorExtension(true);
      } else {
        onLoadFile && onLoadFile(attachment);
      }
      // biome-ignore lint/style/useBlockStatements: single-line early return
    } else handleState();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (file) {
      handleOnChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const bodyLabel =
    'dark:tw-text-neutral-light-100 tw-text-neutral-light-800 tw-font-body5';
  const legalsLabel = 'tw-font-subtitle6 tw-text-neutral-light-600';
  const errorLabel =
    'tw-font-subtitle6 dark:tw-text-alert-300 tw-text-alert-400';

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const contentFile = useMemo(() => {
    if (!fileName) {
      return (
        <>
          {errorExtension || error ? (
            <div>
              <AlertCircle size={20} className="tw-text-alert-400 tw-mb-0.8" />
              <p className="tw-font-subtitle3 tw-text-alert-400 tw-font-semibold">
                {errorExtension || error}
              </p>
              <p className="tw-font-body4 tw-text-alert-400">
                {errorExtension
                  ? labelErrorExtensionDesc
                  : t('ERROR_SIZE', { interpolation: { size: maxSize } })}
              </p>
              <p className="tw-text-link-s tw-text-link-icon tw-pt-1.2">
                <Refresh size={20} />
                {labelReplaceFile ?? t('FILE_REEMPLACE_CUSTOM')}
              </p>
            </div>
          ) : (
            <>
              <div className="tw-mb-0.8">
                {label ?? t('FILE_UPLOAD_CUSTOM')}
              </div>
              <span className={legalsLabel}>{labelMaxSize}</span>
              {error && <span className={errorLabel}>{error}</span>}
            </>
          )}
        </>
      );
    }
    return fileName && progressFile && isLoading ? (
      <div className="tw-flex tw-flex-col tw-place-items-center tw-w-full tw-max-w-28">
        <Loader size="s" />
        <div className="tw-flex tw-items-center tw-my-1.2">
          <FileText size={20} className="tw-text-neutral-light-600 tw-mr-0.4" />
          <p className={bodyLabel}>{fileName}</p>
        </div>
        <ProgressBar size="s" progress={progressFile.progress} />
        <p className="tw-mt-1.2 tw-font-body5 tw-text-neutral-light-700">
          {t('FILE_TIME', {
            interpolation: {
              progress: progressFile.progress,
              duration: durationUpFile,
            },
          })}
        </p>
      </div>
    ) : (
      <>
        <div className="tw-flex tw-items-center">
          <FileText size={20} className="tw-text-neutral-light-600 tw-mr-0.4" />
          <p className={bodyLabel}>{fileName}</p>
        </div>
        <span className={legalsLabel}>{bytesToSize(fileSize)}</span>
        <p className="tw-text-link-s tw-text-link-icon tw-pt-1.2">
          <Refresh size={20} />
          {labelReplaceFile ?? t('FILE_REEMPLACE_CUSTOM')}
        </p>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    durationUpFile,
    error,
    errorExtension,
    fileName,
    fileSize,
    isLoading,
    progressFile,
  ]);

  return (
    <>
      <label
        htmlFor="input-file"
        className=" tw-cursor-pointer tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-h-[180px] tw-bg-transparent tw-px-0.4 tw-text-center tw-rounded-2 tw-border-[2px] tw-border-dashed tw-border-neutral-light-400 hover:tw-border-neutral-light-500 tw-transition-colors tw-ease-in-out tw-duration-100"
        onDragEnter={e => handleOnChange(undefined, e)}
        onDragLeave={e => handleOnChange(undefined, e)}
        onDragOver={e => handleOnChange(undefined, e)}
        onDrop={e => handleOnChange(undefined, e)}
      >
        {contentFile}
      </label>
      <input
        type="file"
        id="input-file"
        data-testid="file-input"
        max={maxSize}
        accept={accept}
        className="tw-hidden"
        onChange={event => handleOnChange(event)}
      />
    </>
  );
};

export default FileInputV2;
